/**
 * CredentialTestingTab Component
 * Provides connection testing interface for credentials
 */

import React, { useState } from 'react';
import { CheckCircle2, XCircle, Loader2, Play, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { credentialsApi } from '@/lib/api';
import { toast } from 'sonner';
import { getCredentialDefinition } from '@/utils/credentialDefinitions';

interface TestResult {
  success: boolean;
  message: string;
  data?: any;
  details?: any;
}

/** Standard masked value for sensitive fields */
const MASKED_VALUE = '••••••••';

interface CredentialTestingTabProps {
  /** Credential type */
  credentialType: string;

  /** Current form data to test */
  formData: any;

  /** Existing credential ID (for edit mode) */
  credentialId?: string;

  /** Callback to build test payload from form data */
  buildTestPayload: () => { type: string; data: any } | null;
}

/**
 * Testing tab content with connection test functionality
 *
 * @example
 * <CredentialTestingTab
 *   credentialType="sql_server"
 *   formData={formData}
 *   credentialId="123"
 *   buildTestPayload={() => ({ type: 'sql_server', data: { ... } })}
 * />
 */
export const CredentialTestingTab: React.FC<CredentialTestingTabProps> = ({
  credentialType,
  formData,
  credentialId,
  buildTestPayload,
}) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const definition = getCredentialDefinition(credentialType);
  const isTestable = definition.testable;

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      let response;

      // If editing an existing credential, test using stored values
      if (credentialId) {
        response = await credentialsApi.test(credentialId);
      } else {
        // For new credentials, build and validate the test payload
        const testPayload = buildTestPayload();

        if (!testPayload) {
          // buildTestPayload returned null, meaning validation failed
          setTesting(false);
          return;
        }

        // Filter out any masked values (shouldn't happen for new credentials, but just in case)
        const filteredData: any = {};
        for (const [key, value] of Object.entries(testPayload.data)) {
          if (value !== MASKED_VALUE) {
            filteredData[key] = value;
          }
        }

        response = await credentialsApi.testDirect({
          type: testPayload.type,
          data: filteredData,
        });
      }

      const result = response.data;

      if (result.success === false) {
        const errorMessage = result.message || result.error || 'Connection test failed';
        setTestResult({
          success: false,
          message: errorMessage,
          data: result.data,
          details: result.details,
        });
        toast.error(errorMessage);
      } else {
        setTestResult({
          success: true,
          message: result.message || 'Connection test successful!',
          data: result.data,
          details: result.details,
        });
        toast.success('Connection test successful!');
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Connection test failed';
      setTestResult({
        success: false,
        message: errorMessage,
        details: error.response?.data?.details,
      });
      toast.error(errorMessage);
    } finally {
      setTesting(false);
    }
  };

  if (!isTestable) {
    return (
      <div className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Testing not available</AlertTitle>
          <AlertDescription>
            Connection testing is not available for {definition.label} credentials.
            The credential will be validated when used in a workflow.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Test Button */}
      <div className="flex flex-col items-center gap-4 py-4">
        <p className="text-sm text-muted-foreground text-center max-w-md">
          Test your {definition.label} connection to verify the credentials are working correctly.
        </p>
        <Button
          onClick={handleTestConnection}
          disabled={testing}
          size="lg"
          className="min-w-[200px]"
        >
          {testing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Testing...
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              Test Connection
            </>
          )}
        </Button>
      </div>

      {/* Test Result */}
      {testResult && (
        <Alert variant={testResult.success ? 'default' : 'destructive'}>
          {testResult.success ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          <AlertTitle>
            {testResult.success ? 'Connection Successful' : 'Connection Failed'}
          </AlertTitle>
          <AlertDescription>
            <p>{testResult.message}</p>

            {/* Show additional details if available */}
            {testResult.details && (
              <div className="mt-3 p-3 bg-muted/50 rounded-md">
                <p className="text-xs font-mono whitespace-pre-wrap">
                  {typeof testResult.details === 'string'
                    ? testResult.details
                    : JSON.stringify(testResult.details, null, 2)}
                </p>
              </div>
            )}

            {/* Show response data if available */}
            {testResult.data && testResult.success && (
              <div className="mt-3 p-3 bg-muted/50 rounded-md">
                <p className="text-xs text-muted-foreground mb-1">Response:</p>
                <p className="text-xs font-mono whitespace-pre-wrap">
                  {typeof testResult.data === 'string'
                    ? testResult.data
                    : JSON.stringify(testResult.data, null, 2)}
                </p>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Tips */}
      <div className="text-xs text-muted-foreground space-y-1">
        <p>Tips:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Make sure all required fields are filled in correctly</li>
          <li>Check that network connectivity to the service is available</li>
          <li>Verify that any firewalls allow the connection</li>
        </ul>
      </div>
    </div>
  );
};

export default CredentialTestingTab;
